import { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { CustomerInfo, PurchasesOffering, PurchasesPackage } from '@revenuecat/purchases-capacitor'
import { Capacitor } from '@capacitor/core'
import { Button } from '../../../components/ui'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card'
import { revenueCat } from '../api/revenueCat'

interface PaywallProps {
  isOpen: boolean
  onClose?: () => void
  onPurchased?: (customerInfo: CustomerInfo) => void | Promise<void>
  /** If true, shows "Skip for now" instead of hiding close entirely */
  showSkip?: boolean
}

type TierId = 'monthly' | 'yearly' | 'lifetime'

const FALLBACK_PLANS: Record<TierId, { cadence: string; description: string; price: string; badge?: string }> = {
  monthly: {
    cadence: 'Month to month',
    description: 'Flexible access for ongoing sessions and trainer support.',
    price: '$14.99',
  },
  yearly: {
    cadence: 'Best value',
    description: 'Commit for the year and lock in the lowest effective monthly price.',
    price: '$119.99',
    badge: 'Most Popular',
  },
  lifetime: {
    cadence: 'One-time unlock',
    description: 'Permanent premium access with no renewals.',
    price: '$249.99',
  },
}

const FEATURE_POINTS = [
  'Unlimited session booking after your free intro',
  'Priority workout scheduling and program updates',
  'Progress history, trainer support, and restores across devices',
]

const findTierPackage = (offering: PurchasesOffering | null, tier: TierId) => {
  if (!offering) return null

  const matchers: Record<TierId, string[]> = {
    lifetime: ['$rc_lifetime', 'lifetime'],
    monthly: ['$rc_monthly', 'month'],
    yearly: ['$rc_annual', '$rc_yearly', 'annual', 'year'],
  }

  const found = offering.availablePackages.find((pkg) =>
    matchers[tier].some((matcher) => pkg.identifier.toLowerCase().includes(matcher))
  ) ?? null
  
  return found
}

const formatSavings = (monthlyPkg: PurchasesPackage | null, yearlyPkg: PurchasesPackage | null) => {
  const monthly = monthlyPkg?.product.price
  const yearly = yearlyPkg?.product.price

  if (!monthly || !yearly) return null

  const savings = Math.round((((monthly * 12) - yearly) / (monthly * 12)) * 100)
  return savings > 0 ? `Save ${savings}%` : null
}

export function Paywall({ isOpen, onClose, onPurchased, showSkip = false }: PaywallProps) {
  const [offering, setOffering] = useState<PurchasesOffering | null>(null)
  const [loading, setLoading] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [activeAction, setActiveAction] = useState<string | null>(null)
  const [debugInfo, setDebugInfo] = useState<string>('')
  const [showDebug, setShowDebug] = useState(false)
  const isSupportedPlatform = revenueCat.isSupportedPlatform()

  useEffect(() => {
    if (!isOpen) return

    setLoading(true)
    setActionError(null)
    
    const platformInfo = [
      `Platform: ${Capacitor.getPlatform()}`,
      `isNativePlatform: ${Capacitor.isNativePlatform()}`,
      `isSupportedPlatform: ${isSupportedPlatform}`,
      `isConfigured (before): ${revenueCat.isConfigured()}`,
    ].join('\n')
    
    setDebugInfo(`${platformInfo}\n\nConfiguring & loading offerings...`)

    // getOfferings now auto-configures if needed
    revenueCat.getOfferings().then((allOfferings) => {
      const offeringKeys = allOfferings?.all ? Object.keys(allOfferings.all) : []
      const hasCurrent = !!allOfferings?.current
      const configuredAfter = revenueCat.isConfigured()
      
      return revenueCat.getCurrentOffering().then((currentOffering) => {
        const updatedPlatformInfo = [
          `Platform: ${Capacitor.getPlatform()}`,
          `isNativePlatform: ${Capacitor.isNativePlatform()}`,
          `isSupportedPlatform: ${isSupportedPlatform}`,
          `isConfigured: ${configuredAfter}`,
          `API key: ${revenueCat.getApiKey()}`,
        ].join('\n')

        if (!currentOffering) {
          const debug = [
            updatedPlatformInfo,
            '',
            '❌ No offering found!',
            '',
            `Raw offerings data:`,
            `• .current exists: ${hasCurrent}`,
            `• .all keys: [${offeringKeys.join(', ') || 'none'}]`,
            '',
            'Check RevenueCat dashboard:',
            '1. Is offering marked "Current"?',
            '2. Are products attached to packages?',
            '3. Is App Store shared secret configured?',
          ].join('\n')
          setDebugInfo(debug)
          setOffering(null)
          return
        }

        const packages = currentOffering.availablePackages || []
        const packageInfo = packages.map(p => 
          `• ${p.identifier}\n  product: ${p.product.identifier}\n  price: ${p.product.priceString}`
        ).join('\n\n')

        const debug = [
          updatedPlatformInfo,
          '',
          `✅ Offering: "${currentOffering.identifier}"`,
          ``,
          `📦 ${packages.length} package(s):`,
          packageInfo || '(none)',
          ``,
          `Available offerings: [${offeringKeys.join(', ')}]`,
        ].join('\n')

        setDebugInfo(debug)
        setOffering(currentOffering)
      })
    }).catch((error) => {
      const debug = [
        `Platform: ${Capacitor.getPlatform()}`,
        `isNativePlatform: ${Capacitor.isNativePlatform()}`,
        `isConfigured: ${revenueCat.isConfigured()}`,
        '',
        `❌ Error: ${error?.message || error}`,
      ].join('\n')
      setDebugInfo(debug)
      setActionError('Unable to load subscription plans right now.')
    }).finally(() => {
      setLoading(false)
    })
  }, [isOpen, isSupportedPlatform])

  const packages = useMemo(() => {
    const monthly = findTierPackage(offering, 'monthly')
    const yearly = findTierPackage(offering, 'yearly')
    const lifetime = findTierPackage(offering, 'lifetime')

    return { lifetime, monthly, yearly }
  }, [offering])

  const savingsLabel = useMemo(() => formatSavings(packages.monthly, packages.yearly), [packages])

  const handlePurchase = async (pkg: PurchasesPackage | null, tier: TierId) => {
    if (!pkg) {
      const availableIds = offering?.availablePackages.map(p => p.identifier).join(', ') || 'none'
      setActionError(`${tier} plan not found. Available: [${availableIds}]. Tap "Debug Info" for details.`)
      return
    }

    setActionError(null)
    setActiveAction(tier)

    try {
      const customerInfo = await revenueCat.purchasePackage(pkg)

      if (!customerInfo?.entitlements.active.pro) {
        setActionError('Purchase did not unlock pro access.')
        return
      }

      await onPurchased?.(customerInfo)
      onClose?.()
    } catch (error: any) {
      setActionError(error?.message || 'Purchase failed. Please try again.')
    } finally {
      setActiveAction(null)
    }
  }

  const handleRestore = async () => {
    setActionError(null)
    setActiveAction('restore')

    try {
      const customerInfo = await revenueCat.restorePurchases()

      if (!customerInfo?.entitlements.active.pro) {
        setActionError('No active pro subscription was found to restore.')
        return
      }

      await onPurchased?.(customerInfo)
      onClose?.()
    } finally {
      setActiveAction(null)
    }
  }

  const tiers: Array<{
    badge?: string
    description: string
    helper: string
    packageData: PurchasesPackage | null
    tier: TierId
    title: string
  }> = [
    {
      tier: 'monthly',
      title: 'Monthly',
      badge: undefined,
      helper: FALLBACK_PLANS.monthly.cadence,
      description: FALLBACK_PLANS.monthly.description,
      packageData: packages.monthly,
    },
    {
      tier: 'yearly',
      title: 'Yearly',
      badge: savingsLabel || FALLBACK_PLANS.yearly.badge,
      helper: FALLBACK_PLANS.yearly.cadence,
      description: FALLBACK_PLANS.yearly.description,
      packageData: packages.yearly,
    },
    {
      tier: 'lifetime',
      title: 'Lifetime',
      badge: undefined,
      helper: FALLBACK_PLANS.lifetime.cadence,
      description: FALLBACK_PLANS.lifetime.description,
      packageData: packages.lifetime,
    },
  ]

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-end justify-center overflow-y-auto overscroll-contain p-4 sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop - always closeable */}
          <motion.button
            aria-label="Close paywall"
            className="fixed inset-0 bg-slate-950/70 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            className="relative z-[81] my-auto w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, y: 32, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
          >
            <Card
              variant="elevated"
              padding="none"
              className="overflow-hidden border border-white/15 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.24),_transparent_35%),linear-gradient(135deg,rgba(15,23,42,0.96),rgba(15,23,42,0.88))] text-white shadow-2xl shadow-slate-950/50"
            >
              <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.08),transparent_35%,rgba(59,130,246,0.08))]" />
              <div className="relative grid gap-6 p-6 sm:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div className="max-w-2xl space-y-3">
                    <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-sky-200">
                      Premium Access
                    </span>
                    <div>
                      <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                        Keep booking after your free intro session.
                      </h2>
                      <p className="mt-2 text-sm text-slate-300 sm:text-base">
                        Unlock ongoing coaching, session scheduling, and your full client experience.
                      </p>
                    </div>
                  </div>
                  {/* Always show close button */}
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-full border border-white/10 bg-white/5 p-2 text-slate-300 transition hover:bg-white/10 hover:text-white"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid gap-4 lg:grid-cols-[1.15fr,0.85fr]">
                  <div className="grid gap-4 md:grid-cols-3">
                    {tiers.map((tier, index) => {
                      const priceString = tier.packageData?.product.priceString || FALLBACK_PLANS[tier.tier].price
                      const isFeatured = tier.tier === 'yearly'
                      const hasPackage = !!tier.packageData

                      return (
                        <motion.div
                          key={tier.tier}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.08 * index }}
                        >
                          <Card
                            padding="lg"
                            className={[
                              'h-full border text-left backdrop-blur-xl',
                              isFeatured
                                ? 'border-sky-300/40 bg-sky-400/15 shadow-lg shadow-sky-950/25'
                                : 'border-white/10 bg-white/5',
                            ].join(' ')}
                          >
                            <CardHeader className="space-y-3">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <CardTitle className="text-white">
                                    {tier.title}
                                    {!hasPackage && <span className="ml-2 text-xs text-red-400">(missing)</span>}
                                  </CardTitle>
                                  <CardDescription className="text-slate-300">{tier.helper}</CardDescription>
                                </div>
                                {tier.badge && (
                                  <span className="rounded-full bg-sky-300 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-950">
                                    {tier.badge}
                                  </span>
                                )}
                              </div>
                              <div>
                                <p className="text-3xl font-semibold text-white">{priceString}</p>
                                <p className="mt-2 text-sm text-slate-300">{tier.description}</p>
                              </div>
                            </CardHeader>
                            <CardContent className="pt-6">
                              <Button
                                variant={isFeatured ? 'primary' : 'secondary'}
                                className="w-full"
                                disabled={!isSupportedPlatform || activeAction !== null}
                                isLoading={activeAction === tier.tier}
                                onClick={() => handlePurchase(tier.packageData, tier.tier)}
                              >
                                {isSupportedPlatform ? `Choose ${tier.title}` : 'Available in iOS app'}
                              </Button>
                            </CardContent>
                          </Card>
                        </motion.div>
                      )
                    })}
                  </div>

                  <motion.div
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl"
                  >
                    <h3 className="text-lg font-semibold text-white">What premium unlocks</h3>
                    <ul className="mt-4 space-y-3">
                      {FEATURE_POINTS.map((feature) => (
                        <li key={feature} className="flex gap-3 text-sm text-slate-200">
                          <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300">
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-6 rounded-2xl border border-white/10 bg-slate-950/30 p-4 text-sm text-slate-300">
                      <p className="font-medium text-white">Restore purchases</p>
                      <p className="mt-1">
                        Already subscribed on this Apple ID? Restore to unlock premium without buying again.
                      </p>
                      <Button
                        variant="ghost"
                        className="mt-4 w-full border border-white/10 bg-white/5 text-white hover:bg-white/10"
                        disabled={!isSupportedPlatform || activeAction !== null || loading}
                        isLoading={activeAction === 'restore'}
                        onClick={handleRestore}
                      >
                        Restore Purchases
                      </Button>
                    </div>

                    {!isSupportedPlatform && (
                      <p className="mt-4 text-xs text-slate-400">
                        RevenueCat purchases are enabled for the native iOS app. Web and unsupported platforms skip billing.
                      </p>
                    )}
                  </motion.div>
                </div>

                {(loading || actionError) && (
                  <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200">
                    {loading ? 'Loading subscription plans...' : actionError}
                  </div>
                )}

                {/* Debug toggle button */}
                <button
                  type="button"
                  onClick={() => setShowDebug(!showDebug)}
                  className="text-xs text-slate-500 hover:text-slate-300 transition underline"
                >
                  {showDebug ? 'Hide Debug Info' : 'Show Debug Info'}
                </button>

                {/* Debug panel */}
                {showDebug && (
                  <div className="rounded-2xl border border-yellow-500/30 bg-yellow-950/30 px-4 py-3 text-xs font-mono text-yellow-200 whitespace-pre-wrap overflow-x-auto">
                    {debugInfo || 'No debug info yet...'}
                  </div>
                )}

                {/* Skip for now link */}
                {showSkip && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="mt-2 text-center text-sm text-slate-400 hover:text-slate-300 transition"
                  >
                    Skip for now
                  </button>
                )}
              </div>
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
